import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { type FullHeadMPRPaneLayout } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Icon, Tooltip } from '@/shared/ui';

import { toothModel } from '@/entities/tooth';

import {
  MPRAnalyticsScreen,
  MPRPaneLayoutViewer,
  MPR_NEXT_PANE_LAYOUT,
  MPR_PANE_LAYOUT_ICONS,
  MPR_TOOL_ICON_SIZE,
  MPR_TOGGLE_EVENTS,
  i18n,
} from '../../config';
import {
  readFullHeadMprPaneLayout,
  writeFullHeadMprPaneLayout,
} from '../../lib';

type MPRPaneLayoutControlProps = {
  viewer: MPRPaneLayoutViewer;
  screen: MPRAnalyticsScreen;
  isDisabled?: boolean;
  toothID?: string;
  className?: string;
};

export const MPRPaneLayoutControl: FC<MPRPaneLayoutControlProps> = (props) => {
  const { viewer, screen, isDisabled = false, toothID, className } = props;
  const { formatMessage } = useIntl();

  const ISONumber = useAppSelector(
    toothModel.selectors.selectISONumberByID(toothID ?? ''),
  );

  const [layout, setLayout] = useState<FullHeadMPRPaneLayout | null>(null);

  useEffect(() => {
    if (!isDisabled) {
      setLayout(viewer.getPaneLayout());
    }
  }, [isDisabled]);

  // Before the viewer is ready its layout is unknown; the saved preference is
  // what gets applied on start, so it drives the icon until then.
  const currentLayout = layout ?? readFullHeadMprPaneLayout();
  const targetLayout = MPR_NEXT_PANE_LAYOUT[currentLayout];
  const label = formatMessage(i18n.mprPaneLayoutToggleMessages[targetLayout]);

  const handleClick = () => {
    if (isDisabled) {
      return;
    }

    viewer.setPaneLayout(targetLayout);
    setLayout(targetLayout);
    writeFullHeadMprPaneLayout(targetLayout);

    trackEvent({
      eventName: MPR_TOGGLE_EVENTS[screen].paneLayout,
      properties: {
        ...(ISONumber === undefined ? {} : { tooth_number: ISONumber }),
        pane_layout: targetLayout,
      },
    });
  };

  return (
    <Tooltip.Primary content={label}>
      <button
        type="button"
        className={className}
        aria-label={label}
        disabled={isDisabled}
        onClick={handleClick}
      >
        <Icon
          size={MPR_TOOL_ICON_SIZE}
          name={MPR_PANE_LAYOUT_ICONS[targetLayout]}
        />
      </button>
    </Tooltip.Primary>
  );
};
